import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Tab,
  Tabs,
} from '@mui/material';
import { usePageTitle } from '@/shared/context/PageTitleContext';
import { radius } from '@/shared/theme/tokens';
import { Paper } from '@/shared/ui';
import { mergeSx } from '@/shared/ui/utils/mergeSx';
import { toolbarActionsSx } from './toolbarActionsSx';

export interface SettingsPageTab {
  label: string;
  icon?: React.ReactElement;
  disabled?: boolean;
}

export interface SettingsPageProps {
  title: string;
  tabs: SettingsPageTab[];
  tabValue: number;
  onTabChange: (value: number) => void;
  toolbarLeft?: React.ReactNode;
  toolbarRight?: React.ReactNode;
  loading?: boolean;
  loadingFallback?: React.ReactNode;
  error?: string | null;
  maxWidth?: 'md' | 'lg';
  /** Wrap tab body in elevated Paper. Disable for tabs that bring their own surface. */
  contentPaper?: boolean;
  /** Fill leftover viewport height (split panes, inner scroll). */
  fillHeight?: boolean;
  children: React.ReactNode;
}

const SettingsPage: React.FC<SettingsPageProps> = ({
  title,
  tabs,
  tabValue,
  onTabChange,
  toolbarLeft,
  toolbarRight,
  loading = false,
  loadingFallback,
  error,
  maxWidth = 'lg',
  contentPaper = true,
  fillHeight = false,
  children,
}) => {
  usePageTitle(title);

  const showToolbar = Boolean(toolbarLeft || toolbarRight);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    onTabChange(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth={maxWidth}>
        <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', py: 8 }}>
          {loadingFallback ?? <CircularProgress />}
        </Box>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        ...(fillHeight
          ? {
              flex: '1 1 0',
              width: '100%',
              alignSelf: 'stretch',
              overflow: 'hidden',
            }
          : {}),
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: '#fff',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Container maxWidth={maxWidth}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="settings tabs"
            sx={{ '& .MuiTab-root': { textTransform: 'none' } }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.label}
                label={tab.label}
                icon={tab.icon}
                iconPosition={tab.icon ? 'start' : undefined}
                disabled={tab.disabled}
              />
            ))}
          </Tabs>
        </Container>
      </Box>

      {showToolbar && (
        <Container maxWidth={maxWidth}>
          <Box
            sx={mergeSx(
              {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                mt: 2,
                mb: 1,
              },
              toolbarActionsSx,
            )}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
              }}
            >
              {toolbarLeft}
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {toolbarRight}
            </Box>
          </Box>
        </Container>
      )}

      <Container
        maxWidth={maxWidth}
        sx={{
          mt: showToolbar ? 0 : 3,
          mb: fillHeight ? 3 : 4,
          ...(fillHeight
            ? {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }
            : {}),
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
            {error}
          </Alert>
        )}

        {contentPaper ? (
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: radius.sm, border: 1, borderColor: 'divider' }}
          >
            {children}
          </Paper>
        ) : fillHeight ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {children}
          </Box>
        ) : (
          children
        )}
      </Container>
    </Box>
  );
};

export default SettingsPage;
